import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { coupleMonogram } from './SacredArtifacts';
import { ConfettiBurst } from './WowEffects';
import { EmeraldFireflies } from './SignatureEffects';
import './wow-effects.css';

type UnboxingGateProps = {
  title: string;
  accent?: string;
  background?: string;
  textureUrl?: string;
  sealLabel?: string;
  children: React.ReactNode;
};

/**
 * Zareqia / WeddingWaliDidi-inspired sealed invite:
 * envelope flaps + embossed wax seal monogram → content.
 */
export function UnboxingGate({
  title,
  accent = '#D4AF37',
  background = '#0a0f0c',
  textureUrl,
  sealLabel = 'You are invited',
  children,
}: UnboxingGateProps) {
  const [opened, setOpened] = useState(false);
  const [burst, setBurst] = useState(false);
  const mono = coupleMonogram(title);

  const open = () => {
    setOpened(true);
    setBurst(true);
  };

  const cardBorder = `1px solid ${accent}77`;
  const cardShadow = `0 28px 90px rgba(0,0,0,0.6), 0 0 40px ${accent}22, inset 0 0 0 1px ${accent}28`;
  const sealGlow = `
    0 10px 0 #2a1a08,
    0 18px 36px rgba(0,0,0,0.55),
    inset 0 3px 10px rgba(255,255,255,0.4),
    inset 0 -8px 16px rgba(0,0,0,0.4),
    0 0 40px ${accent}55
  `;

  return (
    <div className="relative min-h-[100dvh] w-full" data-testid="unboxing-root">
      <ConfettiBurst active={burst} variant="champagne" />
      <AnimatePresence mode="wait">
        {!opened ? (
          <motion.div
            key="gate"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 1.06, filter: 'blur(8px)' }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0 z-[100] flex flex-col items-center justify-center px-5 overflow-hidden"
            style={{ background }}
            data-testid="unboxing-gate"
            onClick={(e) => e.stopPropagation()}
          >
            {textureUrl ? (
              <div
                className="pointer-events-none absolute inset-0 opacity-50"
                style={{
                  backgroundImage: `url(${textureUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'saturate(1.15) brightness(0.5)',
                }}
                aria-hidden
              />
            ) : null}
            <EmeraldFireflies count={16} />
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background: `radial-gradient(ellipse at 50% 35%, ${accent}40 0%, transparent 52%)`,
              }}
              aria-hidden
            />

            <p
              className="relative z-10 mb-5 text-center text-xs font-medium uppercase tracking-[0.42em]"
              style={{ color: accent, fontFamily: 'Fraunces, Georgia, serif' }}
            >
              A sealed invitation
            </p>

            <div className="relative z-10 w-full max-w-[21rem]" style={{ perspective: 900 }}>
              <div
                className="absolute -top-10 left-0 right-0 h-14 origin-bottom"
                style={{
                  clipPath: 'polygon(0 100%, 50% 0, 100% 100%)',
                  background: `linear-gradient(180deg, ${accent}55, #0a1a14)`,
                  borderBottom: `1px solid ${accent}66`,
                  zIndex: 2,
                }}
                aria-hidden
              />

              <motion.div
                className="relative overflow-hidden rounded-sm"
                style={{
                  aspectRatio: '3 / 4',
                  background: 'linear-gradient(155deg, #0f281f 0%, #041510 55%, #020a07 100%)',
                  border: cardBorder,
                  boxShadow: cardShadow,
                }}
              >
                <motion.div
                  className="pointer-events-none absolute inset-0 opacity-40"
                  style={{
                    background:
                      'linear-gradient(105deg, transparent 40%, rgba(255,230,150,0.35) 50%, transparent 60%)',
                  }}
                  animate={{ x: ['-60%', '120%'] }}
                  transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut', repeatDelay: 1.2 }}
                  aria-hidden
                />

                <span
                  className="absolute left-3 top-3 h-9 w-9 border-l-2 border-t-2"
                  style={{ borderColor: accent }}
                  aria-hidden
                />
                <span
                  className="absolute right-3 top-3 h-9 w-9 border-r-2 border-t-2"
                  style={{ borderColor: accent }}
                  aria-hidden
                />
                <span
                  className="absolute bottom-3 left-3 h-9 w-9 border-b-2 border-l-2"
                  style={{ borderColor: accent }}
                  aria-hidden
                />
                <span
                  className="absolute bottom-3 right-3 h-9 w-9 border-b-2 border-r-2"
                  style={{ borderColor: accent }}
                  aria-hidden
                />

                <div className="absolute inset-0 flex flex-col items-center justify-center px-6 pt-10">
                  <p
                    className="mb-3 text-center text-[10px] uppercase tracking-[0.35em] text-white/50"
                    style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
                  >
                    {sealLabel}
                  </p>
                  <p
                    className="mb-8 max-w-[14rem] text-center text-xl leading-snug"
                    style={{ color: accent, fontFamily: 'Fraunces, Georgia, serif' }}
                  >
                    {title || 'Open Invitation'}
                  </p>

                  <motion.button
                    type="button"
                    aria-label="Break the wax seal to open the invitation"
                    onClick={(e) => {
                      e.stopPropagation();
                      open();
                    }}
                    className="relative flex h-32 w-32 flex-col items-center justify-center rounded-full"
                    style={{
                      background: `radial-gradient(circle at 32% 28%, ${accent} 0%, #8B6914 42%, #4a2f0c 100%)`,
                      boxShadow: sealGlow,
                    }}
                    whileHover={{ scale: 1.06, y: -3 }}
                    whileTap={{ scale: 0.92, y: 4 }}
                    animate={{ scale: [1, 1.03, 1] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <span
                      className="text-2xl font-bold tracking-wide"
                      style={{
                        color: '#1a1208',
                        fontFamily: 'Fraunces, Georgia, serif',
                        textShadow: '0 1px 0 rgba(255,255,255,0.3)',
                      }}
                    >
                      {mono}
                    </span>
                    <span
                      className="mt-1 text-[9px] uppercase tracking-[0.2em]"
                      style={{ color: '#2a1a08' }}
                    >
                      wax seal
                    </span>
                  </motion.button>

                  <p className="mt-7 text-[10px] uppercase tracking-[0.28em] text-white/55">
                    Tap seal to open
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0, y: 28, rotateX: 8 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
